/**
 * Icon - render a `lucide` IconNode as an inline <svg>, the React sibling of
 * designSystem.createIcon (same stroke geometry, same a11y contract). The
 * `lucide` package's icon exports are plain `[tag, attrs][]` data, so no
 * lucide-react dependency is needed - both UIs draw from ONE icon set.
 */
import { createElement } from 'react';
import type { ReactElement } from 'react';
import type { LucideIconNode } from '../../utils/iconCanvas';

export interface IconProps {
  icon: LucideIconNode;
  size?: number;
  strokeWidth?: number;
  color?: string;
  fill?: string;
  /** Accessible name; omitted = decorative (aria-hidden). */
  label?: string;
}

export function Icon({
  icon,
  size = 16,
  strokeWidth = 2,
  color = 'currentColor',
  fill = 'none',
  label,
}: IconProps): ReactElement {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill={fill}
      stroke={color}
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      role={label ? 'img' : undefined}
      aria-label={label}
      aria-hidden={label ? undefined : true}
    >
      {icon.map(([tag, attrs], i) => createElement(tag, { key: i, ...attrs }))}
    </svg>
  );
}
